import { Component, ViewChild, ElementRef} from '@angular/core';
import * as papaparse from 'papaparse';
import { Observable, Subscription } from 'rxjs';

import { AbstractDialogComponent } from '../../../../Core/Dialogs/components/AbstractDialog/abstract-dialog.component';
import { CoreDialogService } from '../../../../Core/Dialogs/services/dialog.service';
import { validateEmail } from '../../../Misc/helpers/formatting/email.helper';
import { AppearCondition, AutocompleteCustomOption } from '../../../Forms/components/Autocomplete/autocomplete-custom-option.model';
import { ButtonTypesEnum } from '../../../Forms/enums/button-types.enum';
import { RecipientModel } from '../../models/recipient.model';
import { MailingListModel } from '../../models/mailingList.model';
import { MailingListsService } from '../../services/mailing-lists.service';

@Component({
    selector: 'agpr-import-recipients-dialog',
    templateUrl: './import-recipients-dialog.component.html',
    styleUrls: ['./import-recipients-dialog.component.scss'],
    standalone: false
})
export class ImportRecipientsDialogComponent extends AbstractDialogComponent {
    readonly buttonTypes = ButtonTypesEnum;
    readonly selectAllCheckboxId = 'importRecipientsDialog_selectAll';

    private subscriptions: Subscription[] = [];

    hasFile: boolean = false;
    fileError: boolean = false;
    loading: boolean = false;

    selectAll: boolean = false;
    recipients: RecipientModel[] = [];
    selectedRecipients: RecipientModel[] = [];
    failedRows: string[][];

    mailingLists: MailingListModel[] = [];
    mailingLists$: Observable<MailingListModel[]>;
    selectedMailingList: MailingListModel;

    @ViewChild('fileInput') fileInput: ElementRef<HTMLInputElement>;

    customOptions: AutocompleteCustomOption[] = [
        {
            label: 'Common.mailingLists.importDialog.mailingList.create',
            action: (searchString: string) => {
                const mailingListToCreate = new MailingListModel();
                mailingListToCreate.name = searchString;
                this.mailingListsService.createMailingList(mailingListToCreate).subscribe(
                    createdList => {
                        this.selectedMailingList = createdList;
                    }
                );
            },
            appearCondition: AppearCondition.noExactMatchInSuggestions,
            clearOnSelect: true
        }
    ];

    constructor(
        public coreDialogService: CoreDialogService,
        private mailingListsService: MailingListsService
    ) {
        super(coreDialogService, 'ImportRecipients');
        this.mailingLists$ = this.mailingListsService.getMailingLists();

        this.subscriptions.push(
            this.mailingLists$.subscribe(lists => {
                this.mailingLists = lists;
            })
        );
    }

    /**
     * Action when the user clicks the "OK" button
     */
    ok(): void {
        if (this.selectedRecipients.length < 1) {
            return;
        }
        this.loading = true;
        this.mailingListsService.importRecipients(this.selectedRecipients, this.selectedMailingList).subscribe({
            next: () => {
                this.loading = false;
                this.closeDialog(true);
            },
            error: () => {
                this.loading = false;
            }
        });
    }

    /**
     * Action when the user clicks the "Cancel" button, or closes the dialog via the "X" in the header
     */
    cancel(): void {
        this.closeDialog(false);
    }

    browse(): void {
        this.fileInput.nativeElement.click();
    }

    handleFileInput(file: File): void {
        if (!file) {
            return;
        }

        this.fileError = false;
        this.loading = true;
        papaparse.parse<string[]>(file, {
            complete: (results) => {
                this.loading = false;

                const csv: string[][] = results.data;
                const maybeHeadingRow = csv[0];
                let hasHeadingRow;

                let emailColumnIndex: number = this.findEmailHeadingColumnIndex(maybeHeadingRow);
                let nameColumnIndexes: number[];

                if (emailColumnIndex >= 0) {
                    hasHeadingRow = true;
                    nameColumnIndexes = this.findNameHeadingColumnIndexes(maybeHeadingRow);
                } else {
                    emailColumnIndex = this.findEmailColumnIndex(csv[0]);
                }

                if (emailColumnIndex < 0) {
                    this.fileError = true;
                    return;
                }

                const dataRows: string[][] = hasHeadingRow ? csv.slice(1) : csv.slice();
                const failedRows: string[][] = [];
                const recipients: RecipientModel[] = [];

                dataRows.forEach(row => {
                    const email = row[emailColumnIndex]?.trim();

                    if (!validateEmail(email)) {
                        failedRows.push(row);
                        return;
                    }

                    const recipient = new RecipientModel();
                    recipient.email = email;

                    if (nameColumnIndexes && nameColumnIndexes.length > 0) {
                        recipient.name = nameColumnIndexes
                            .map(i => row[i]) // map the name rows in order
                            .filter(namePart => !!('' + namePart).trim()) // filter out the empty ones
                            .join(' '); // join them together
                    }

                    recipients.push(recipient);
                });
                if (recipients.length < 1) {
                    this.fileError = true;
                    return;
                }
                this.hasFile = true;
                this.recipients = recipients;
                this.selectedRecipients = recipients.slice();
                this.selectAll = true;
                this.failedRows = failedRows;
                requestAnimationFrame(() => {
                    (document.querySelector('#' + this.selectAllCheckboxId + '_checkbox') as HTMLElement).focus();
                });
            }
        });
    }

    findEmailHeadingColumnIndex(row: string[]): number {
        return row.findIndex(column => {
            return !!('' + column).toLocaleLowerCase().match(/e-?mail/);
        });
    }
    findEmailColumnIndex(row: string[]): number {
        return row.findIndex(column => {
            return validateEmail(column);
        });
    }
    findNameHeadingColumnIndexes(row: string[]): number[] {
        let indexes: number[] = [];
        for (let i = 0, l = row.length; i < l; i++) {
            const lower = ('' + row[i]).toLocaleLowerCase();
            if (lower.includes('name')) {
                // This part tries to put the names in correct order if they're split
                if (lower.includes('first')) {
                    indexes.unshift(i);
                } else if (lower.includes('last')) {
                    indexes.push(i);
                } else if (lower.includes('full')) {
                    indexes = [i];
                    break;
                } else {
                    indexes.push(i);
                }
            }
        }
        return indexes;
    }

    toggleSelectAll(): void {
        if (this.recipients.length === this.selectedRecipients.length) {
            this.selectAll = false;
            this.selectedRecipients = [];
        } else {
            this.selectAll = true;
            this.selectedRecipients = this.recipients.slice();
        }
    }

    checkSelectAll(): void {
        this.selectAll = this.recipients.length === this.selectedRecipients.length;
    }
}
